/**
 * VideoCustomControls.jsx — P2 (YouTube IFrame Player API integration)
 *
 * Drop-in YouTube player with a custom controls bar:
 *   ⏵ play / pause     · time elapsed / total     · scrub track (seek)
 *   speed selector (1× / 1.25× / 1.5× / 2×)    · captions toggle (cc)
 *   fullscreen
 *
 * Vimeo videos fall back to the basic iframe — Vimeo has its own
 * Player.js library and the P2 spec only contracted custom controls for
 * YouTube (where related-video / branding suppression is the dominant
 * UX concern).  Vimeo iframes already carry `title=0&byline=0&portrait=0`
 * for the same effect.
 *
 * Feature flag:
 *   - DEFAULT: ON for YouTube videos
 *   - `localStorage.lp_p2_controls = "0"`  forces fallback to basic iframe
 *   - `REACT_APP_LP_CUSTOM_CONTROLS = "false"` env override (build-time)
 *
 * Spec §4C of /app/specs/LEARNING_PATHS_VIDEO_LIBRARY_SPEC.md.
 */
import { useEffect, useRef, useState, useCallback } from "react";
import { Play, Pause, Maximize, Captions, Gauge } from "lucide-react";

const SPEEDS = [1, 1.25, 1.5, 2];

/* ─────────────────────────────────────────────────────────────────────
 *  Feature flag
 * ─────────────────────────────────────────────────────────────────── */
export function isCustomControlsEnabled() {
  try {
    // Explicit env-level kill switch wins (build-time)
    if (process.env.REACT_APP_LP_CUSTOM_CONTROLS === "false") return false;
    // Per-browser kill switch (user-toggleable for QA / rollback)
    if (typeof window !== "undefined") {
      const local = localStorage.getItem("lp_p2_controls");
      if (local === "0") return false;
      if (local === "1") return true;
    }
    // Default: ON for P2 launch
    return true;
  } catch { return true; }
}

/* ─────────────────────────────────────────────────────────────────────
 *  YouTube IFrame API loader — single shared Promise per page
 * ─────────────────────────────────────────────────────────────────── */
let _ytReadyPromise = null;
function loadYouTubeAPI() {
  if (_ytReadyPromise) return _ytReadyPromise;
  _ytReadyPromise = new Promise((resolve) => {
    if (typeof window === "undefined") { resolve(null); return; }
    if (window.YT && window.YT.Player) { resolve(window.YT); return; }
    // YouTube polls for the global callback — chain it instead of
    // clobbering whatever might already be registered.
    const prev = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      if (typeof prev === "function") { try { prev(); } catch { /* ignore */ } }
      resolve(window.YT);
    };
    const tag = document.createElement("script");
    tag.src = "https://www.youtube.com/iframe_api";
    tag.async = true;
    document.head.appendChild(tag);
  });
  return _ytReadyPromise;
}

/* ─────────────────────────────────────────────────────────────────────
 *  Time formatter (mm:ss / hh:mm:ss)
 * ─────────────────────────────────────────────────────────────────── */
function fmt(secs) {
  if (!Number.isFinite(secs) || secs < 0) return "0:00";
  const s = Math.floor(secs);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const ss = s % 60;
  return h > 0 ? `${h}:${String(m).padStart(2, "0")}:${String(ss).padStart(2, "0")}`
               : `${m}:${String(ss).padStart(2, "0")}`;
}

/* ─────────────────────────────────────────────────────────────────────
 *  Custom YouTube player + controls bar
 * ─────────────────────────────────────────────────────────────────── */
export default function VideoCustomControls({ videoId, title, onProgress }) {
  const containerRef = useRef(null);       // wrapper for fullscreen
  const mountRef = useRef(null);            // div YT replaces with its iframe
  const playerRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [pos, setPos] = useState(0);
  const [dur, setDur] = useState(0);
  const [speed, setSpeed] = useState(1);
  const [ccOn, setCcOn] = useState(false);
  const [dragging, setDragging] = useState(false);

  // ── P3 — Listen for external seek requests (e.g. transcript click-to-seek).
  // Decoupled via a window event so the transcript tab doesn't need a
  // ref into this component.  Event detail: `{ videoId?, t_sec }`.
  // The `videoId` is optional — if provided we only seek when it matches
  // our active video (prevents stale clicks from a previous chapter).
  useEffect(() => {
    function onSeekRequest(e) {
      const detail = e?.detail || {};
      if (detail.videoId && detail.videoId !== videoId) return;
      const p = playerRef.current;
      if (!p) return;
      try {
        p.seekTo(Math.max(0, parseFloat(detail.t_sec) || 0), true);
        p.playVideo?.();
      } catch { /* ignore */ }
    }
    window.addEventListener("learning-video:seek", onSeekRequest);
    return () => window.removeEventListener("learning-video:seek", onSeekRequest);
  }, [videoId]);

  // ── Mount the YT player once we have the API + a video id ────────────
  useEffect(() => {
    if (!videoId) return undefined;
    let cancelled = false;
    let player;
    loadYouTubeAPI().then((YT) => {
      if (cancelled || !YT || !mountRef.current) return;
      player = new YT.Player(mountRef.current, {
        videoId,
        playerVars: {
          // Same suppression params as the embed-URL builder.
          rel: 0, modestbranding: 1, showinfo: 0,
          cc_load_policy: 0, iv_load_policy: 3, fs: 1,
          // Our custom bar owns the UI — hide the native one.
          controls: 0, disablekb: 0, playsinline: 1,
        },
        events: {
          onReady: (e) => {
            playerRef.current = e.target;
            setReady(true);
            try { setDur(e.target.getDuration() || 0); } catch { /* ignore */ }
          },
          onStateChange: (e) => {
            // YT.PlayerState: PLAYING=1, PAUSED=2, ENDED=0, BUFFERING=3
            setPlaying(e.data === 1);
            if (e.data === 0) setPlaying(false);
          },
          onPlaybackRateChange: (e) => setSpeed(e.data),
        },
      });
    });
    return () => {
      cancelled = true;
      try { player?.destroy?.(); } catch { /* ignore */ }
      playerRef.current = null;
      setReady(false); setPlaying(false); setPos(0); setDur(0); setSpeed(1); setCcOn(false);
    };
  }, [videoId]);

  // ── Tick: poll current time while playing (and once per second while
  // paused so the scrubber stays in sync with seek-on-pause).
  useEffect(() => {
    if (!ready) return undefined;
    const id = window.setInterval(() => {
      const p = playerRef.current;
      if (!p) return;
      try {
        const cur = p.getCurrentTime() || 0;
        if (!dragging) setPos(cur);
        const d = p.getDuration() || 0;
        if (d && d !== dur) setDur(d);
        // P3 — Surface progress to parent for auto-mark-complete.
        if (onProgress && d > 0) {
          try { onProgress({ pos: cur, dur: d, pct: cur / d, playing }); } catch { /* ignore */ }
        }
      } catch { /* ignore */ }
    }, 500);
    return () => window.clearInterval(id);
  }, [ready, dragging, dur, onProgress, playing]);

  // ── Controls
  const toggle = useCallback(() => {
    const p = playerRef.current;
    if (!p) return;
    try { if (playing) p.pauseVideo(); else p.playVideo(); } catch { /* ignore */ }
  }, [playing]);

  const seekTo = useCallback((sec) => {
    const p = playerRef.current;
    if (!p) return;
    try { p.seekTo(Math.max(0, Math.min(sec, dur)), true); setPos(sec); } catch { /* ignore */ }
  }, [dur]);

  const cycleSpeed = useCallback(() => {
    const p = playerRef.current;
    if (!p) return;
    const idx = SPEEDS.indexOf(speed);
    const next = SPEEDS[(idx + 1) % SPEEDS.length];
    try { p.setPlaybackRate(next); setSpeed(next); } catch { /* ignore */ }
  }, [speed]);

  const toggleCC = useCallback(() => {
    const p = playerRef.current;
    if (!p) return;
    try {
      if (!ccOn) {
        p.loadModule("captions");
        p.setOption("captions", "reload", true);
        p.setOption("captions", "track", { languageCode: "en" });
        setCcOn(true);
      } else {
        p.unloadModule("captions");
        setCcOn(false);
      }
    } catch { /* ignore — track may not exist */ }
  }, [ccOn]);

  const toggleFullscreen = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    try {
      if (document.fullscreenElement) document.exitFullscreen?.();
      else el.requestFullscreen?.();
    } catch { /* ignore */ }
  }, []);

  // Keyboard: spacebar = play/pause, ←/→ = seek ±5s
  const onKeyDown = useCallback((e) => {
    if (e.target?.tagName === "INPUT" || e.target?.tagName === "TEXTAREA") return;
    if (e.code === "Space") { e.preventDefault(); toggle(); }
    else if (e.code === "ArrowLeft") { e.preventDefault(); seekTo(pos - 5); }
    else if (e.code === "ArrowRight") { e.preventDefault(); seekTo(pos + 5); }
  }, [toggle, seekTo, pos]);

  const pct = dur > 0 ? (pos / dur) * 100 : 0;

  return (
    <div ref={containerRef} className="bg-black" tabIndex={0} onKeyDown={onKeyDown}
         data-testid="video-custom-controls" data-ready={ready ? "1" : "0"}>
      <div className="aspect-video w-full bg-black">
        {/* YT replaces this div with its iframe on player creation */}
        <div ref={mountRef} className="w-full h-full" title={title || "Video"} />
      </div>
      <div className="flex items-center gap-3 px-4 py-2 bg-slate-900 text-white text-xs"
           data-testid="video-controls-bar">
        <button onClick={toggle} disabled={!ready} className="p-1 hover:bg-white/10 rounded disabled:opacity-40"
                aria-label={playing ? "Pause" : "Play"} data-testid="video-play-pause">
          {playing ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
        </button>
        <span className="font-mono tabular-nums w-[88px]" data-testid="video-time">
          {fmt(pos)} / {fmt(dur)}
        </span>
        <input
          type="range" min={0} max={Math.max(dur, 1)} step={0.1} value={pos}
          onChange={(e) => setPos(parseFloat(e.target.value))}
          onMouseDown={() => setDragging(true)}
          onTouchStart={() => setDragging(true)}
          onMouseUp={(e) => { setDragging(false); seekTo(parseFloat(e.target.value)); }}
          onTouchEnd={(e) => { setDragging(false); seekTo(parseFloat(e.target.value)); }}
          disabled={!ready || !dur}
          className="flex-1 h-1 accent-white cursor-pointer disabled:opacity-30"
          aria-label="Seek"
          data-testid="video-scrub"
          style={{ background: `linear-gradient(to right, white 0%, white ${pct}%, rgba(255,255,255,0.2) ${pct}%, rgba(255,255,255,0.2) 100%)` }}
        />
        <button onClick={cycleSpeed} disabled={!ready} className="flex items-center gap-1 px-2 py-1 hover:bg-white/10 rounded disabled:opacity-40"
                aria-label={`Playback speed ${speed}x`} data-testid="video-speed">
          <Gauge className="h-3 w-3" /> <span className="font-mono">{speed}×</span>
        </button>
        <button onClick={toggleCC} disabled={!ready}
                className={`p-1 rounded disabled:opacity-40 ${ccOn ? "bg-white/20" : "hover:bg-white/10"}`}
                aria-label="Toggle captions" data-testid="video-cc"
                title="Captions (if available)">
          <Captions className="h-3.5 w-3.5" />
        </button>
        <button onClick={toggleFullscreen} className="p-1 hover:bg-white/10 rounded"
                aria-label="Fullscreen" data-testid="video-fullscreen">
          <Maximize className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}
